import { FunctionComponent } from 'react';
import {
  OnboardingOverview,
  OnboardingResultsAreIn,
  OnboardingOurPredictions,
  OnboardingPredictedCareNeeds,
  OnboardingInsightsSummary,
  OnboardingCostFactors,
  OnboardingCarePhasesPhaseOne,
  OnboardingCareSettingsPhaseOne,
  OnboardingFamilyCaregivingPhaseOne,
  OnboardingCareSettingsPhaseTwo,
  OnboardingFamilyCaregivingPhaseTwo,
  OnboardingCareSettingsPhaseThree,
  OnboardingFamilyCaregivingPhaseThree,
  OnboardingCarePhasesSummary,
  OnboardingFundingSources,
  OnboardingPolicyApplicationDashboard,
  OnboardingFundingCoverage,
  OnboardingDownloadReport,
} from '@/pages';
import { OnboardingD2CPaywall } from '@/pages/OnboardingD2CPaywall';
import { OnboardingD2CPaymentSuccess } from '@/pages/OnboardingD2CPaymentSuccess';
import {
  CareEnvironment,
  CarePhase,
  carePhaseDefs,
  SessionType,
  usesFamilyCaregivingStep,
  resolveUseClaimProjectionEngine,
} from '@shared';
import { GENWORTH_POLICY_EDUCATION_SLIDE_DEFS } from '@/features/genworth/education/policy-education-slides';

// Genworth-only policy-education slides, inserted before the funding-sources step and
// filtered out for everyone else (see getFilteredSlides).
const GENWORTH_POLICY_EDUCATION_SLIDES: FunctionComponent<ClientIdProps>[] =
  GENWORTH_POLICY_EDUCATION_SLIDE_DEFS.map(def => def.component);

export function getFirstCarePhasePage(_client: Client) {
  return OnboardingCarePhasesPhaseOne;
}

export function getCareSettingsPageBy(
  carePhase: CarePhase,
): FunctionComponent<ClientIdProps> | undefined {
  switch (carePhaseDefs[carePhase].index) {
    case 0:
      return OnboardingCareSettingsPhaseOne;
    case 1:
      return OnboardingCareSettingsPhaseTwo;
    case 2:
      return OnboardingCareSettingsPhaseThree;
    default:
      return undefined;
  }
}

export function getFamilyInvolvementPageBy(
  carePhase: CarePhase,
): FunctionComponent<ClientIdProps> | undefined {
  switch (carePhaseDefs[carePhase].index) {
    case 0:
      return OnboardingFamilyCaregivingPhaseOne;
    case 1:
      return OnboardingFamilyCaregivingPhaseTwo;
    case 2:
      return OnboardingFamilyCaregivingPhaseThree;
    default:
      return undefined;
  }
}

export function getCarePhasePageBy(
  _carePhase: CarePhase,
): FunctionComponent<ClientIdProps> | undefined {
  return OnboardingCarePhasesPhaseOne;
}

export interface SlideStepConfig {
  id: string;
  title: string;
  beginningSlideIndex: number;
  lastSlideIndex: number;
  isOptional?: boolean;
}

export const ONBOARDING_SLIDES: FunctionComponent<ClientIdProps>[] = [
  OnboardingOverview,
  OnboardingResultsAreIn,
  OnboardingOurPredictions,
  OnboardingPredictedCareNeeds,
  OnboardingInsightsSummary,
  OnboardingD2CPaywall,
  OnboardingD2CPaymentSuccess,
  OnboardingCostFactors,
  OnboardingCarePhasesPhaseOne,
  OnboardingCareSettingsPhaseOne,
  OnboardingFamilyCaregivingPhaseOne,
  OnboardingCareSettingsPhaseTwo,
  OnboardingFamilyCaregivingPhaseTwo,
  OnboardingCareSettingsPhaseThree,
  OnboardingFamilyCaregivingPhaseThree,
  OnboardingCarePhasesSummary,
  ...GENWORTH_POLICY_EDUCATION_SLIDES,
  OnboardingFundingSources,
  OnboardingPolicyApplicationDashboard,
  OnboardingFundingCoverage,
  OnboardingDownloadReport,
];

export type StepDef = {
  name: 'Review Results' | 'Personalize Plan' | 'Finance Costs';
  beginningSlideIndex: number;
  lastSlideIndex: number;
};

export function getStepDefs(client: Client, isD2C = false): StepDef[] {
  const clientFirstCarePhasePage = getFirstCarePhasePage(client);

  const steps: StepDef[] = [
    {
      name: 'Review Results',
      beginningSlideIndex: ONBOARDING_SLIDES.indexOf(OnboardingResultsAreIn),
      lastSlideIndex: ONBOARDING_SLIDES.indexOf(OnboardingCostFactors),
    },
    {
      name: 'Personalize Plan',
      beginningSlideIndex: ONBOARDING_SLIDES.indexOf(clientFirstCarePhasePage),
      lastSlideIndex: ONBOARDING_SLIDES.indexOf(OnboardingCarePhasesSummary),
    },
  ];

  if (!isD2C) {
    steps.push({
      name: 'Finance Costs',
      beginningSlideIndex: ONBOARDING_SLIDES.indexOf(OnboardingFundingSources),
      lastSlideIndex: ONBOARDING_SLIDES.indexOf(OnboardingFundingCoverage),
    });
  }

  return steps;
}

export const SLIDE_TITLES = new Map<FunctionComponent<ClientIdProps>, string>([
  [OnboardingOverview, 'Overview'],
  [OnboardingResultsAreIn, 'Results Are In'],
  [OnboardingOurPredictions, 'Our Predictions'],
  [OnboardingPredictedCareNeeds, 'Predicted Care Needs'],
  [OnboardingInsightsSummary, 'Insights Summary'],
  [OnboardingD2CPaywall, 'Access Full Planning'],
  [OnboardingD2CPaymentSuccess, 'Payment Success'],
  [OnboardingCostFactors, 'Estimated LTC Costs'],
  [OnboardingCarePhasesPhaseOne, 'Care Timeline'],
  [OnboardingCareSettingsPhaseOne, 'Early Care Settings'],
  [OnboardingFamilyCaregivingPhaseOne, 'Early Family Caregiving'],
  [OnboardingCareSettingsPhaseTwo, 'Moderate Care Settings'],
  [OnboardingFamilyCaregivingPhaseTwo, 'Moderate Family Caregiving'],
  [OnboardingCareSettingsPhaseThree, 'Full Care Settings'],
  [OnboardingFamilyCaregivingPhaseThree, 'Full Family Caregiving'],
  [OnboardingCarePhasesSummary, 'Cost Total'],
  [OnboardingFundingSources, 'Financing Plan'],
  [OnboardingPolicyApplicationDashboard, 'Application Review'],
  [OnboardingFundingCoverage, 'Funding Coverage'],
  [OnboardingDownloadReport, 'Download Report'],
  ...GENWORTH_POLICY_EDUCATION_SLIDE_DEFS.map(
    def => [def.component, def.title] as [FunctionComponent<ClientIdProps>, string],
  ),
]);

export const SLIDE_VIDEO_URLS = new Map<FunctionComponent<ClientIdProps>, string>([
  [OnboardingResultsAreIn, 'https://www.loom.com/embed/ba5e6a8ee0ea43af948f2b3f9acd3cc6'],
  [OnboardingOurPredictions, 'https://www.loom.com/embed/7658b90ecbb54cf887ae42fa3bab1a36'],
  [OnboardingPredictedCareNeeds, 'https://www.loom.com/embed/c7c2c346cf0644a2abed74e43c627368'],
  [OnboardingCostFactors, 'https://www.loom.com/embed/1d5609f345194b1ea83965e71966a94a'],
  [OnboardingCarePhasesPhaseOne, 'https://www.loom.com/embed/cb0db5c296e545388dc6652b17f4c7eb'],
  [OnboardingCareSettingsPhaseOne, 'https://www.loom.com/embed/3ae72ed2bbd34bd58085314810f3692f'],
  [
    OnboardingFamilyCaregivingPhaseOne,
    'https://www.loom.com/embed/105ffa8741ee41979ab1de5f03f4e181',
  ],
  [OnboardingCareSettingsPhaseTwo, 'https://www.loom.com/embed/5331f668f77a4d019f1e87ab7af343d1'],
  [
    OnboardingFamilyCaregivingPhaseTwo,
    'https://www.loom.com/embed/bd6031210d04453cabb52dd9236a1314',
  ],
  [OnboardingCareSettingsPhaseThree, 'https://www.loom.com/embed/d2e76c4356184cdfb048e2bebf3b0526'],
  [
    OnboardingFamilyCaregivingPhaseThree,
    'https://www.loom.com/embed/9beb759bcb3747f6a935bca2fbff3326',
  ],
  [OnboardingCarePhasesSummary, 'https://www.loom.com/embed/540ea4d8ee5148ffa7bc6cd600a4113a'],
]);

const CARE_PLANNING_SLIDES: FunctionComponent<ClientIdProps>[] = [
  OnboardingCarePhasesPhaseOne,
  OnboardingCareSettingsPhaseOne,
  OnboardingCareSettingsPhaseTwo,
  OnboardingCareSettingsPhaseThree,
  OnboardingFamilyCaregivingPhaseOne,
  OnboardingFamilyCaregivingPhaseTwo,
  OnboardingFamilyCaregivingPhaseThree,
];

const D2C_ONLY_SLIDES: FunctionComponent<ClientIdProps>[] = [
  OnboardingD2CPaywall,
  OnboardingD2CPaymentSuccess,
];

const D2C_PAYWALL_SLIDES: FunctionComponent<ClientIdProps>[] = [
  OnboardingD2CPaywall,
];

const D2C_EXCLUDED_SLIDES: FunctionComponent<ClientIdProps>[] = [
  OnboardingFundingSources,
  OnboardingPolicyApplicationDashboard,
];

const EDJ_PILOT_EXCLUDED_SLIDES: FunctionComponent<ClientIdProps>[] = [
  OnboardingPolicyApplicationDashboard,
];

// Genworth drops the apply-for-a-policy surface, so the application-review slide is removed.
const GENWORTH_EXCLUDED_SLIDES: FunctionComponent<ClientIdProps>[] = [
  OnboardingPolicyApplicationDashboard,
];

/**
 * In the d2c app mode the paywall hands off to the guided consumer flow, which replaces every
 * slide after it, until the consumer pays to self-fund and unlocks those slides.
 */
export function consumerFlowReplacesSlides(
  appMode: AppMode | undefined,
  clientStripe: ClientStripeSession | null | undefined,
): boolean {
  return appMode === 'd2c' && !clientStripe?.hasActivePlan;
}

export function getFilteredSlides(
  client: Client,
  featureFlags: FeatureFlags,
  careEnvironmentSelections?: Partial<Record<CarePhase, CareEnvironment | null>>,
  appConfigFlags?: AppConfigFeatureFlags,
  sessionType?: SessionType,
  clientStripe?: ClientStripeSession | null,
  appMode?: AppMode,
): FunctionComponent<ClientIdProps>[] {
  let slides = getBaseFilteredSlides(client, featureFlags);

  // Policy-education slides only appear in the Genworth flow.
  if (!featureFlags.genworthMode) {
    slides = slides.filter(slide => !GENWORTH_POLICY_EDUCATION_SLIDES.includes(slide));
  }

  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;

  if (!isD2C) {
    slides = slides.filter(slide => slide !== OnboardingInsightsSummary);
  }

  if (isD2C) {
    slides = slides.filter(slide => !D2C_EXCLUDED_SLIDES.includes(slide));

    if (consumerFlowReplacesSlides(appMode, clientStripe)) {
      slides = slides.slice(0, slides.indexOf(OnboardingD2CPaywall) + 1);
    } else if (clientStripe?.hasActivePlan) {
      slides = slides.filter(slide => !D2C_PAYWALL_SLIDES.includes(slide));
    }
  } else {
    slides = slides.filter(slide => !D2C_ONLY_SLIDES.includes(slide));
  }

  if (featureFlags.edjPilotAdvisor) {
    slides = slides.filter(slide => !EDJ_PILOT_EXCLUDED_SLIDES.includes(slide));
  }

  if (featureFlags.genworthMode) {
    slides = slides.filter(slide => !GENWORTH_EXCLUDED_SLIDES.includes(slide));
  }

  const { clientHasStartedLtc, clientCurrentCarePhase } = client.intakeSurvey ?? {};
  const useClaimProjectionEngine = resolveUseClaimProjectionEngine(client);

  slides = slides.filter(slide => {
    if (!CARE_PLANNING_SLIDES.includes(slide)) {
      return true;
    }

    if (
      excludeFamilyCaregivingNonHomeCarePhases(
        slide,
        useClaimProjectionEngine,
        careEnvironmentSelections,
      )
    ) {
      return false;
    }

    if (clientHasStartedLtc && clientCurrentCarePhase) {
      return !excludePreviousCarePhaseSlidesForOnClaim(slide, clientCurrentCarePhase);
    }

    return true;
  });

  return slides;
}

function getBaseFilteredSlides(
  client: Client,
  featureFlags: FeatureFlags,
): FunctionComponent<ClientIdProps>[] {
  if (!client.intakeSurvey?.customHiddenFields) {
    return ONBOARDING_SLIDES;
  }

  const { customHiddenFields } = client.intakeSurvey;

  if (featureFlags.vbShop && customHiddenFields.customSkipCarePlanning === 'true') {
    return excludeSlidesByStepName(client, 'Personalize Plan');
  }

  return ONBOARDING_SLIDES;
}

function excludeSlidesByStepName(
  client: Client,
  stepName: StepDef['name'],
): FunctionComponent<ClientIdProps>[] {
  const stepDefs = getStepDefs(client);
  const step = stepDefs.find(s => s.name === stepName)!;

  const beginningSlideIndex =
    stepName === 'Personalize Plan'
      ? ONBOARDING_SLIDES.indexOf(OnboardingCarePhasesPhaseOne)
      : step.beginningSlideIndex;
  const lastSlideIndex = step.lastSlideIndex;

  return ONBOARDING_SLIDES.filter((_slide, index) => {
    return index < beginningSlideIndex || index > lastSlideIndex;
  });
}

function excludeFamilyCaregivingNonHomeCarePhases(
  slide: FunctionComponent<ClientIdProps>,
  useClaimProjectionEngine: boolean,
  careEnvironmentSelections?: Partial<Record<CarePhase, CareEnvironment | null>>,
): boolean {
  const carePhaseForSlide =
    slide === OnboardingFamilyCaregivingPhaseOne
      ? CarePhase.earlyCare
      : slide === OnboardingFamilyCaregivingPhaseTwo
        ? CarePhase.moderateCare
        : slide === OnboardingFamilyCaregivingPhaseThree
          ? CarePhase.fullCare
          : null;

  if (carePhaseForSlide === null) {
    return false;
  }

  // Keep the family-caregiving slide for phases whose environment runs the step
  // — home, or Independent Living (home-style care the family can offset).
  // Exclude it otherwise. This must agree with the next-slide routing, or the
  // sequence drops the slide and the routing override is rejected.
  return !usesFamilyCaregivingStep(
    careEnvironmentSelections?.[carePhaseForSlide],
    useClaimProjectionEngine,
  );
}

function excludePreviousCarePhaseSlidesForOnClaim(
  slide: FunctionComponent<ClientIdProps>,
  clientCurrentCarePhase: CarePhase,
): boolean {
  const currentPhaseIndex = carePhaseDefs[clientCurrentCarePhase].index;

  // The care-timeline slide covers every phase at once, so it is never behind
  // the client — only the per-phase settings and caregiving slides can be.
  if (slide === OnboardingCarePhasesPhaseOne) {
    return false;
  }

  if (slide === OnboardingCareSettingsPhaseOne || slide === OnboardingFamilyCaregivingPhaseOne) {
    return currentPhaseIndex > 0;
  }

  if (slide === OnboardingCareSettingsPhaseTwo || slide === OnboardingFamilyCaregivingPhaseTwo) {
    return currentPhaseIndex > 1;
  }

  return false;
}

export function isFilteredFromFullList(
  filteredSlides: FunctionComponent<ClientIdProps>[],
): boolean {
  return filteredSlides.length !== ONBOARDING_SLIDES.length;
}

export function isBaseFilteredFromFullList(
  client: Client,
  featureFlags: FeatureFlags,
): boolean {
  return getBaseFilteredSlides(client, featureFlags).length !== ONBOARDING_SLIDES.length;
}

export function isD2CPaywallLocked(
  slide: FunctionComponent<ClientIdProps>,
  filteredSlides: FunctionComponent<ClientIdProps>[],
  isD2C: boolean,
  clientStripe: ClientStripeSession | null,
): boolean {
  if (!isD2C) return false;

  if (clientStripe?.hasActivePlan) return false;

  const paywallIndex = filteredSlides.indexOf(OnboardingD2CPaywall);
  if (paywallIndex === -1) return false;

  const slideIndex = filteredSlides.indexOf(slide);
  return slideIndex > paywallIndex;
}
